import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { aiUsageAPI } from '@/shared/lib/api';
import { Card, CardHeader, CardTitle, CardContent, Input } from '@travellegacy/nexops-design-system';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DollarSign, Zap, Hash, TrendingUp, Calendar } from 'lucide-react';
import { formatNumber } from '@/lib/utils';
import type { AIUsageSummary } from '@/types';

// Custom Tooltip para valores em USD
const CustomTooltipUSD = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number; name: string; color: string }>;
  label?: string;
}) => {
  if (active && payload && payload.length) {
    const formatUSD = (value: number) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 6,
      }).format(value);
    };

    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
        <p className="text-sm font-medium text-foreground mb-2">{label}</p>
        {payload.map((entry, index: number) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatUSD(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function AIUsagePage() {
  const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>({});

  // Fetch summary data
  const { data: summaryData, isLoading } = useQuery({
    queryKey: ['ai-usage-summary', dateRange],
    queryFn: async () => {
      const response = await aiUsageAPI.getSummary(dateRange);
      return response.data.data as AIUsageSummary;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Cores para gráficos
  const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#6366f1'];

  // Formatar USD
  const formatUSD = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(value);
  };

  // Formatação de tokens
  const formatTokens = (tokens: number) => {
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(2)}M`;
    }
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    }
    return tokens.toString();
  };

  // Nomes dos módulos em português
  const moduleNames: Record<string, string> = {
    'blog': 'Blog',
    'instagram': 'Instagram',
    'training': 'Treinamento',
    'whatsapp-promotion': 'WhatsApp Promoções',
    'onboarding': 'Onboarding',
    'weekly-report': 'Relatório Semanal',
    'whatsapp-analysis': 'Análise WhatsApp',
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <div className="h-9 w-64 bg-muted animate-pulse rounded" />
          <div className="h-5 w-96 bg-muted animate-pulse rounded mt-1" />
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-card rounded-lg border p-6 animate-pulse">
              <div className="h-5 w-32 bg-muted rounded mb-4" />
              <div className="h-8 w-24 bg-muted rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!summaryData) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Nenhum dado disponível</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Custos de IA</h1>
        <p className="text-muted-foreground">
          Monitoramento de uso e custos da API Anthropic (Claude)
        </p>
      </div>

      {/* Filtros de Data */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Input
                label="Data Inicial"
                type="date"
                value={dateRange.startDate || ''}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
            <div className="flex-1">
              <Input
                label="Data Final"
                type="date"
                value={dateRange.endDate || ''}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
            <button
              onClick={() => setDateRange({})}
              className="px-4 py-2 border rounded-md hover:bg-muted mb-1"
            >
              Limpar
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Cards de Resumo */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Custo Total</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatUSD(summaryData.totalCostUSD)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Gastos com API Anthropic
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Tokens</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatTokens(summaryData.totalTokens)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Processados no período
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Chamadas de IA</CardTitle>
            <Hash className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(summaryData.totalCalls)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Requisições realizadas
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Custo Médio</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatUSD(summaryData.totalCalls > 0 ? summaryData.totalCostUSD / summaryData.totalCalls : 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Por chamada de IA
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Custos por Módulo */}
        <Card>
          <CardHeader>
            <CardTitle>Custos por Módulo</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={summaryData.byModule}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="module"
                  tickFormatter={(value) => moduleNames[value] || value}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tickFormatter={(value) => formatUSD(value)} />
                <Tooltip content={<CustomTooltipUSD />} />
                <Legend />
                <Bar dataKey="totalCostUSD" fill="#3b82f6" name="Custo (USD)" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Distribuição por Modelo */}
        <Card>
          <CardHeader>
            <CardTitle>Distribuição por Modelo</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={summaryData.byModel}
                  dataKey="totalCostUSD"
                  nameKey="model"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(data) => `${data.model.split('-')[1]}: ${formatUSD(data.totalCostUSD)}`}
                >
                  {summaryData.byModel.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltipUSD />} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Gráfico de Custos Diários */}
      <Card>
        <CardHeader>
          <CardTitle>Custos Diários</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={summaryData.dailyCosts.slice().reverse()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis tickFormatter={(value) => formatUSD(value)} />
              <Tooltip content={<CustomTooltipUSD />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="totalCostUSD"
                stroke="#3b82f6"
                strokeWidth={2}
                name="Custo (USD)"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Tabela de Operações Mais Caras */}
      <Card>
        <CardHeader>
          <CardTitle>Top Operações Mais Caras</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-muted">
                <tr>
                  <th className="px-4 py-3">Módulo</th>
                  <th className="px-4 py-3">Operação</th>
                  <th className="px-4 py-3 text-right">Custo Total</th>
                  <th className="px-4 py-3 text-right">Chamadas</th>
                  <th className="px-4 py-3 text-right">Custo Médio</th>
                  <th className="px-4 py-3 text-right">Tokens</th>
                </tr>
              </thead>
              <tbody>
                {summaryData.byOperation.slice(0, 10).map((op, index) => (
                  <tr key={index} className="border-b hover:bg-muted/50">
                    <td className="px-4 py-3 font-medium">{moduleNames[op.module] || op.module}</td>
                    <td className="px-4 py-3">{op.operation}</td>
                    <td className="px-4 py-3 text-right font-semibold">{formatUSD(op.totalCostUSD)}</td>
                    <td className="px-4 py-3 text-right">{formatNumber(op.callCount)}</td>
                    <td className="px-4 py-3 text-right">{formatUSD(op.avgCostPerCall)}</td>
                    <td className="px-4 py-3 text-right">{formatTokens(op.totalTokens)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
